/**
 * FinancialSection - EV financial analysis tab
 *
 * Composes CapexEditor + FinancialMetricsCard + ProformaTable + TCOComparison.
 * Calls the backend financial API to compute metrics.
 */

import React, { useState, useCallback } from 'react';
import { Box, Button, CircularProgress, Alert, alpha } from '@mui/material';
import { Calculate as CalculateIcon } from '@mui/icons-material';
import { SectionCard, vortxColors, API_BASE_URL } from '@vortx/shared-ui';
import { CapexEditor, FinancialMetricsCard, ProformaTable, TCOComparison } from '../../../financial';

function FinancialSection({ scenario, projectData, onChange }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [financialResults, setFinancialResults] = useState(null);

    // Local state for capex and ICE baseline (persisted to scenario on change)
    const capexData = scenario?.financial?.capex || {};
    const iceBaseline = scenario?.financial?.ice_baseline || {
        fuel_type: 'diesel',
        price_per_gallon: 4.50,
        fleet_mpg: 6.0,
        maintenance_per_mile: 0.22,
        annual_miles_per_vehicle: 50000,
    };
    const financialParams = scenario?.financial?.params || {};

    const handleCapexChange = useCallback((category, fieldKey, value) => {
        const updated = {
            ...capexData,
            [category]: {
                ...(capexData[category] || {}),
                [fieldKey]: value,
            },
        };
        onChange({
            financial: {
                ...(scenario?.financial || {}),
                capex: updated,
            },
        });
    }, [capexData, onChange, scenario?.financial]);

    const handleIceChange = useCallback((newBaseline) => {
        onChange({
            financial: {
                ...(scenario?.financial || {}),
                ice_baseline: newBaseline,
            },
        });
    }, [onChange, scenario?.financial]);

    const hasSimResults = !!(
        scenario?.results?.strategies?.cost_optimized?.metrics
        || scenario?.results?.strategies?.demand_limit?.metrics
        || scenario?.results?.strategies?.opportunity?.metrics
    );

    const totalCapex = Object.values(capexData).reduce((sum, cat) => {
        if (typeof cat !== 'object') return sum;
        return sum + Object.values(cat).reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0);
    }, 0);

    const handleCalculate = useCallback(async () => {
        if (!hasSimResults) {
            setError('Run simulation first to generate cost data.');
            return;
        }
        if (totalCapex <= 0) {
            setError('Enter capital costs before running financial analysis.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Flatten capex for backend
            const flatCapex = {
                chargers: Object.values(capexData.chargers || {}).reduce((s, v) => s + (v || 0), 0),
                electrical: Object.values(capexData.electrical || {}).reduce((s, v) => s + (v || 0), 0),
                soft_costs: Object.values(capexData.soft_costs || {}).reduce((s, v) => s + (v || 0), 0),
                vehicles: Object.values(capexData.vehicles || {}).reduce((s, v) => s + (v || 0), 0),
            };

            const response = await fetch(
                `${API_BASE_URL}/api/v1/ev/projects/${projectData?.id}/scenarios/${scenario?.id}/financials`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        capex: flatCapex,
                        financial_params: financialParams,
                        ice_baseline: iceBaseline,
                    }),
                },
            );

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || `HTTP ${response.status}`);
            }

            const result = await response.json();
            setFinancialResults(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [hasSimResults, totalCapex, capexData, financialParams, iceBaseline, projectData?.id, scenario?.id]);

    return (
        <Box>
            {!hasSimResults && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Run a simulation first to populate EV operating costs for financial analysis.
                </Alert>
            )}

            {/* CapEx Editor */}
            <SectionCard title="Capital Costs" icon={<CalculateIcon />} sx={{ mb: 3 }}>
                <CapexEditor
                    capexData={capexData}
                    onCapexChange={handleCapexChange}
                />
            </SectionCard>

            {/* TCO Comparison */}
            <SectionCard title="TCO Comparison" sx={{ mb: 3 }}>
                <TCOComparison
                    iceBaseline={iceBaseline}
                    onIceChange={handleIceChange}
                    tcoResults={financialResults?.tco || null}
                    evAnnualCost={hasSimResults}
                />
            </SectionCard>

            {/* Calculate Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
                    onClick={handleCalculate}
                    disabled={loading || !hasSimResults || totalCapex <= 0}
                    sx={{
                        bgcolor: vortxColors.teal,
                        '&:hover': { bgcolor: vortxColors.darkGreen },
                        px: 4,
                    }}
                >
                    {loading ? 'Calculating...' : 'Run Financial Analysis'}
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}

            {/* Financial Metrics */}
            {financialResults?.proforma?.metrics && (
                <FinancialMetricsCard
                    metrics={financialResults.proforma.metrics}
                    systemCosts={{ totalCost: totalCapex }}
                />
            )}

            {/* Pro Forma Cash Flows */}
            {financialResults?.proforma?.cashflows && (
                <SectionCard title="Pro Forma" sx={{ mb: 3 }}>
                    <ProformaTable
                        cashflows={financialResults.proforma.cashflows}
                        totalCapex={totalCapex}
                        metrics={financialResults.proforma.metrics}
                    />
                </SectionCard>
            )}
        </Box>
    );
}

export default FinancialSection;
